import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Checkbox from 'ui/Checkbox';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { SingleDateInput } from 'pureUi/SingleDateInput';

import { TAnnouncementPublishFor, TAnnouncementAppearIn } from 'services/BackendApi/types/AnnouncementResponse';
import { EUserType } from 'services/BackendApi/types/AuthResponse';
import { getCurrentUserType } from 'store/modules/auth';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';

interface IPublishingSettingsSectionProps {
  isDraft: boolean;
  publishFor: TAnnouncementPublishFor;
  activeFrom: string; // ISO date string or empty
  activeTo: string; // ISO date string or empty
  appearIn: TAnnouncementAppearIn[];
  validationErrors: {
    activeFrom?: boolean;
    activeTo?: boolean;
    appearIn?: boolean;
  };
  updateIsDraft: (isDraft: boolean) => void;
  updatePublishFor: (publishFor: TAnnouncementPublishFor) => void;
  updateActiveFrom: (date: string) => void;
  updateActiveTo: (date: string) => void;
  updateAppearIn: (locations: TAnnouncementAppearIn[]) => void;
}

const PUBLISH_FOR_OPTIONS: IMultiselectValueLabelPair[] = [
  { value: 'ALL', label: 'All Users' },
  { value: 'INTERNAL', label: 'Internal users only' },
  { value: 'EXTERNAL', label: 'External users only' },
];

const APPEAR_IN_OPTIONS = [
  { value: 'SEARCH_PAGE', label: 'Hotel Page' },
  { value: 'BASKET', label: 'Basket Page' },
  { value: 'BREAKDOWN', label: 'Breakdown' },
  { value: 'VOUCHER', label: 'Voucher' },
  { value: 'EXPLORE_HOTEL', label: 'Hotels Collection' },
] as const;

export const PublishingSettingsSection: React.FC<IPublishingSettingsSectionProps> = ({
  isDraft,
  publishFor,
  activeFrom,
  activeTo,
  appearIn,
  validationErrors,
  updateIsDraft,
  updatePublishFor,
  updateActiveFrom,
  updateActiveTo,
  updateAppearIn,
}) => {
  const userType = useSelector(getCurrentUserType) as EUserType;
  const dynamicParameters = ParameterService.getParameters();

  // Filter appear-in options based on user type and feature flags
  const filteredAppearInOptions = useMemo(() => {
    return APPEAR_IN_OPTIONS.filter(option => {
      if (option.value === 'EXPLORE_HOTEL') {
        // Show for internal users if internal flag is enabled
        if (userType !== EUserType.TA && dynamicParameters.ENABLE_HOTELS_COLLECTION_MENU_INTERNAL_TEMP) {
          return true;
        }
        // Show for TA users if external flag is enabled
        if (userType === EUserType.TA && dynamicParameters.ENABLE_HOTELS_COLLECTION_MENU_EXTERNAL_TEMP) {
          return true;
        }
        return false;
      }
      return true;
    });
  }, [userType, dynamicParameters.ENABLE_HOTELS_COLLECTION_MENU_INTERNAL_TEMP, dynamicParameters.ENABLE_HOTELS_COLLECTION_MENU_EXTERNAL_TEMP]);

  const handleActiveFromChange = (date: Date) => {
    updateActiveFrom(date.toISOString());
  };

  const handleActiveToChange = (date: Date) => {
    updateActiveTo(date.toISOString());
  };

  const handlePublishForChange = (values: string[]) => {
    if (values.length > 0) {
      updatePublishFor(values[0] as TAnnouncementPublishFor);
    }
  };

  const handleAppearInToggle = (location: TAnnouncementAppearIn) => {
    if (appearIn.includes(location)) {
      updateAppearIn(appearIn.filter(l => l !== location));
    } else {
      updateAppearIn([...appearIn, location]);
    }
  };

  return (
    <div className="flex flex-col gap-[10px] relative">
      {/* Publishing Settings Header */}
      <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Publishing Settings</span>

      {/* Row container with all fields */}
      <div className="flex flex-col md:flex-row items-start gap-[40px]">
        {/* Save as Draft Checkbox */}
        <div className="flex flex-row items-center gap-[8px] w-[118px] self-start md:self-center mt-4">
          <Checkbox id="save-as-draft" checked={isDraft} onChange={e => updateIsDraft(e.currentTarget.checked)} />
          <label htmlFor="save-as-draft" className="font-pt-sans text-base leading-[21px] text-black cursor-pointer">
            Save as Draft
          </label>
        </div>

        {/* Publish For Dropdown */}
        <div className="flex flex-col gap-[5px] w-[250px]">
          <span className="block font-pt-sans text-[13px] leading-[17px] tracking-[0.04em] text-black">
            Publish For
          </span>
          <Multiselect
            options={PUBLISH_FOR_OPTIONS}
            selectedValues={[publishFor]}
            onUpdate={handlePublishForChange}
            isSingleSelectMode={true}
            isCloseOnSelect={true}
            itemsClassname="bg-white-hard"
            itemCtaClassName="hover:bg-gray-15"
            placeholderText="Select"
            height={35}
          />
        </div>

        {/* Active From Date Picker */}
        <div className="flex flex-col gap-[5px] flex-1 max-w-[120px]" data-validation-id="activeFrom">
          <span className="block font-pt-sans text-[13px] leading-[17px] tracking-[0.04em] text-black">
            Active From{!isDraft && ' *'}
          </span>
          <SingleDateInput
            value={activeFrom ? new Date(activeFrom) : null}
            onChange={handleActiveFromChange}
            buttonClassName={classNames(
              'w-full h-[35px] px-[10px] border border-solid font-pt-sans text-[15px] leading-[19px] text-black text-left w-full',
              {
                'bg-white-hard border-gray-40': !validationErrors.activeFrom,
                '!bg-red-25 !border-red-95': validationErrors.activeFrom,
              }
            )}
            enablePastDates={true}
          />
          {validationErrors.activeFrom && (
            <span className="text-[13px] font-pt-sans text-red-95">
              Active From is required when not saving as draft
            </span>
          )}
        </div>

        {/* Active To Date Picker */}
        <div className="flex flex-col gap-[5px] flex-1 max-w-[120px]" data-validation-id="activeTo">
          <span className="block font-pt-sans text-[13px] leading-[17px] tracking-[0.04em] text-black">Active To</span>
          <SingleDateInput
            value={activeTo ? new Date(activeTo) : null}
            onChange={handleActiveToChange}
            buttonClassName={classNames(
              'w-full h-[35px] px-[10px] bg-white-hard border border-solid font-pt-sans text-[15px] leading-[19px] text-black text-left w-full',
              {
                'border-gray-40': !validationErrors.activeTo,
                'border-red-95': validationErrors.activeTo,
              }
            )}
            enablePastDates={true}
          />
          {validationErrors.activeTo && <span className="text-[13px] font-pt-sans text-red-95">Field is required</span>}
        </div>

        {/* Show this Announcement in */}
        <div className="flex flex-col justify-end gap-[5px] flex-1" data-validation-id="appearIn">
          <span
            className={classNames(
              'block font-pt-sans text-black text-[13px] leading-[17px] tracking-[0.04em] whitespace-nowrap',
              {
                // 'text-black': !validationErrors.appearIn,
                // 'text-red-95': validationErrors.appearIn,
              }
            )}
          >
            Show this Announcement in *
          </span>
          <div className="flex flex-row items-center gap-[20px] h-[35px]">
            {filteredAppearInOptions.map(option => (
              <div key={option.value} className="flex flex-row items-center gap-[8px] h-[20px]">
                <Checkbox
                  id={`appear-in-${option.value}`}
                  checked={appearIn.includes(option.value)}
                  onChange={() => handleAppearInToggle(option.value)}
                />
                <label
                  htmlFor={`appear-in-${option.value}`}
                  className="font-pt-sans text-base leading-[21px] text-black cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          {validationErrors.appearIn && (
            <span className="absolute translate-y-[24px] text-[13px] font-pt-sans text-red-95">
              Select at least one option
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishingSettingsSection;
